import { createElement, Dispatch, memo, SetStateAction, useCallback, useMemo } from "react";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Column } from "@tanstack/table-core";
import { Button } from "../../../../../Button";
import { SimpleSelect } from "../../../../../inputs";
import { Tooltip } from "../../../../../Tooltip";
import { useTablePropsContext } from "../../../../context";
import { renderColumnHeader } from "../../../../utils";

import { ReactComponent as TrashIcon } from "./icons/trash.svg";

import styles from "./styles.module.scss";

export type FilterRowProps = {
  column: Column<Object>,
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>,
  setColumnOperators: Dispatch<SetStateAction<ColumnFiltersState>>,
  value: unknown
  operator: unknown
}

export const FilterRow = memo<FilterRowProps>((
  {
    column,
    value,
    operator,
    setColumnFilters,
    setColumnOperators,
  }) => {
  const tableProps = useTablePropsContext();
  const columnId = column.id;

  const operatorOptions = useMemo(() => Array.from(new Set([
    column.columnDef.meta!.defaultFilterFn,
    ...column.columnDef.meta!.additionalFilterFn!,
  ])), [ column.columnDef.meta ]);

  const onChange = useCallback((value: unknown) => {
    setColumnFilters(
      prevState => {
        return (
          prevState.map(x =>
            x.id !== columnId
              ? x
              : (
                (value !== undefined)
                  ? {
                    id: columnId,
                    value,
                  }
                  : undefined
              ),
          )
            .filter(Boolean) as ColumnFiltersState);
      },
    );
  }, [ columnId, setColumnFilters ]);

  const onOperatorChange = useCallback((value: unknown) => {
    setColumnOperators(
      prevState => (
        prevState.map(x =>
          x.id !== columnId
            ? x
            : {
              id: columnId,
              value,
            },
        )
      ),
    );
  }, [ columnId, setColumnOperators ]);

  return (
    <>
      {renderColumnHeader(column)}
      <SimpleSelect<any, false>
        placeholder="Operator"
        options={operatorOptions}
        value={operator}
        onChange={onOperatorChange}
        getOptionLabel={tableProps?.prettifyFilterOperatorLabel}
        minMenuWidth={250}
      />
      <div className={styles.filterBlock}>
        {
          createElement(
            column.columnDef.meta!.Filter!,
            {
              filterFn: operator as any,
              column: column,
              value: value,
              onChange: onChange,
            },
          )
        }
      </div>
      <Tooltip content={"Remove"}>
        <Button
          buttonSize="sm"
          buttonType="tertiaryGray"
          onClick={() => onChange(undefined)}
          iconOnly
        >
          <TrashIcon />
        </Button>
      </Tooltip>
    </>
  );
});